import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, Copy, GripVertical, MoreHorizontal, ChevronLeft, ChevronRight,
  Type, AlignLeft, Hash, ChevronsUpDown, CalendarDays, CheckSquare, Link2, User,
  Pencil, Table, ListChecks, ClipboardPaste,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PageSkeleton } from "@/components/LoadingSkeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const COL_TYPES = [
  { value: "text",      label: "Text",      icon: Type },
  { value: "long_text", label: "Long Text", icon: AlignLeft },
  { value: "number",    label: "Number",    icon: Hash },
  { value: "dropdown",  label: "Dropdown",  icon: ChevronsUpDown },
  { value: "date",      label: "Date",      icon: CalendarDays },
  { value: "checkbox",  label: "Checkbox",  icon: CheckSquare },
  { value: "link",      label: "Link",      icon: Link2 },
  { value: "member",    label: "Member",    icon: User },
];
const COL_TYPE_MAP = Object.fromEntries(COL_TYPES.map((t) => [t.value, t]));

/* ==============================================================
   SidebarList — Google-Sheets-inspired spreadsheet for
   Task Lists & Custom Tables. Features:
   • Sticky header + row-number gutter
   • Keyboard nav (Arrow keys, Tab, Enter)
   • Cell focus ring, alternating row hover
   • Copy (Ctrl+C) & Paste (Ctrl+V) TSV — interoperable with
     Google Sheets / Excel; paste auto-creates rows if needed
   • Column reorder, rename, delete; dropdown option manager
   • Drag-to-reorder rows; duplicate/delete row shortcuts
   ============================================================== */
export default function SidebarList() {
  const { listId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [addColOpen, setAddColOpen] = useState(false);
  const [editingListName, setEditingListName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [editColumn, setEditColumn] = useState(null);
  const [focus, setFocus] = useState(null);       // {r, c}
  const [editingCell, setEditingCell] = useState(null); // {r, c}
  const [selection, setSelection] = useState(null); // {r1,c1,r2,c2} — sheet-style range
  const selDrag = useRef(null); // {startR, startC}
  const colDrag = useRef(null); // dragged column id
  const gridRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [l, r, u] = await Promise.all([
        api.get(`/sidebar-lists/${listId}`),
        api.get(`/sidebar-lists/${listId}/rows`),
        api.get("/users"),
      ]);
      setList(l.data);
      setRows(r.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/dash");
    }
  }, [listId, nav]);
  useEffect(() => { load(); }, [load]);

  const canEdit = user && ["super_admin", "admin", "department_admin", "content_manager", "team_lead"].includes(user.role);
  const cols = list?.columns || [];

  /* ---------- Column CRUD ---------- */
  const addColumn = async ({ name, type, options }) => {
    try {
      const r = await api.post(`/sidebar-lists/${listId}/columns`, { name, type, options });
      setList((l) => ({ ...l, columns: [...(l.columns || []), r.data] }));
      toast.success("Column added");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const patchColumn = async (colId, body) => {
    try {
      const r = await api.patch(`/sidebar-lists/${listId}/columns/${colId}`, body);
      setList((l) => ({ ...l, columns: r.data.columns }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteColumn = async (colId) => {
    if (!window.confirm("Delete this column and all its data?")) return;
    try {
      await api.delete(`/sidebar-lists/${listId}/columns/${colId}`);
      setList((l) => ({ ...l, columns: (l.columns || []).filter((c) => c.id !== colId) }));
      setRows((rs) => rs.map((r) => { const c = { ...(r.cells || {}) }; delete c[colId]; return { ...r, cells: c }; }));
      toast.success("Column deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* ---------- Row CRUD ---------- */
  const addRow = useCallback(async (opts = {}) => {
    try {
      const r = await api.post(`/sidebar-lists/${listId}/rows`, opts);
      setRows((rs) => [...rs, r.data]);
      return r.data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  }, [listId]);
  const duplicateRow = async (rowId) => {
    const r = await addRow({ duplicate_from: rowId });
    if (r) toast.success("Row duplicated");
  };
  const deleteRow = async (rowId) => {
    try {
      await api.delete(`/sidebar-lists/${listId}/rows/${rowId}`);
      setRows((rs) => rs.filter((r) => r.id !== rowId));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const patchCells = useCallback(async (rowId, patchMap) => {
    setRows((rs) => rs.map((r) => r.id === rowId ? { ...r, cells: { ...(r.cells || {}), ...patchMap } } : r));
    try { await api.patch(`/sidebar-lists/${listId}/rows/${rowId}`, { cells: patchMap }); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  }, [listId, load]);

  /* ---------- Row reorder handle (grip only, so drag-select on cells still works) ---------- */
  const rowDrag = useRef(null);
  const onRowHandleDragStart = (e, id) => { rowDrag.current = id; e.dataTransfer.effectAllowed = "move"; };
  const onRowDrop = async (e, targetId) => {
    e.preventDefault();
    const src = rowDrag.current;
    rowDrag.current = null;
    if (!src || src === targetId) return;
    const ids = rows.map((r) => r.id).filter((id) => id !== src);
    const idx = ids.indexOf(targetId);
    ids.splice(idx, 0, src);
    setRows((rs) => ids.map((id) => rs.find((r) => r.id === id)).filter(Boolean));
    try { await api.post(`/sidebar-lists/${listId}/reorder-rows`, { row_ids: ids }); }
    catch (err) { toast.error(formatApiError(err)); load(); }
  };

  /* ---------- Column drag-to-reorder via header ---------- */
  const onColDragStart = (e, colId) => { colDrag.current = colId; e.dataTransfer.effectAllowed = "move"; };
  const onColDrop = async (e, targetColId) => {
    e.preventDefault();
    const src = colDrag.current;
    colDrag.current = null;
    if (!src || src === targetColId) return;
    // Compute the new ordering client-side then persist by moving one step at a time
    // (the backend `move: 'left'|'right'` only swaps neighbours).
    const ids = cols.map((c) => c.id).filter((id) => id !== src);
    const idx = ids.indexOf(targetColId);
    ids.splice(idx, 0, src);
    // Optimistic UI:
    setList((l) => ({ ...l, columns: ids.map((id) => l.columns.find((c) => c.id === id)) }));
    // Persist by nudging until each column is at its target index.
    // Diff-based: walk from left to right, moving 'src' left/right until index matches.
    const currentCols = cols.map((c) => c.id);
    const target = ids;
    const patches = [];
    // Simple approach: repeatedly detect a wrong index and issue moves.
    // For MVP, do a bubble-sort-style set of PATCH move calls.
    const work = [...currentCols];
    for (let i = 0; i < target.length; i++) {
      const desired = target[i];
      const currentIdx = work.indexOf(desired);
      while (work.indexOf(desired) > i) {
        const at = work.indexOf(desired);
        [work[at - 1], work[at]] = [work[at], work[at - 1]];
        patches.push([desired, "left"]);
      }
      while (work.indexOf(desired) < i) {
        const at = work.indexOf(desired);
        [work[at], work[at + 1]] = [work[at + 1], work[at]];
        patches.push([desired, "right"]);
      }
    }
    try {
      for (const [id, dir] of patches) {
        await api.patch(`/sidebar-lists/${listId}/columns/${id}`, { move: dir });
      }
      // Re-fetch once at end to be safe.
      const l = await api.get(`/sidebar-lists/${listId}`);
      setList(l.data);
    } catch (err) { toast.error(formatApiError(err)); load(); }
  };

  /* ---------- Selection: click-drag across cells ---------- */
  const onCellMouseDown = (e, r, c) => {
    if (e.button !== 0) return;
    setFocus({ r, c });
    setEditingCell(null);
    selDrag.current = { r, c };
    setSelection({ r1: r, c1: c, r2: r, c2: c });
  };
  const onCellMouseEnter = (e, r, c) => {
    if (!selDrag.current) return;
    setSelection({ r1: selDrag.current.r, c1: selDrag.current.c, r2: r, c2: c });
  };
  useEffect(() => {
    const onUp = () => { selDrag.current = null; };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);
  // Utility: is this (r,c) inside the current selection?
  const inSelection = (r, c) => {
    if (!selection) return false;
    const { r1, c1, r2, c2 } = selection;
    return r >= Math.min(r1, r2) && r <= Math.max(r1, r2) && c >= Math.min(c1, c2) && c <= Math.max(c1, c2);
  };

  /* ---------- Sheet keyboard nav + copy/paste ---------- */
  const moveFocus = useCallback((dr, dc) => {
    if (!focus) return;
    const nR = rows.length;
    const nC = cols.length;
    let r = focus.r + dr;
    let c = focus.c + dc;
    if (r < 0) r = 0;
    if (c < 0) c = 0;
    if (c >= nC) c = nC - 1;
    if (r >= nR) r = nR - 1;
    if (r < 0 || c < 0) return;
    setFocus({ r, c });
    setEditingCell(null);
  }, [focus, rows.length, cols.length]);

  const copyFocus = useCallback(async () => {
    if (!focus) return false;
    const sel = selection || { r1: focus.r, c1: focus.c, r2: focus.r, c2: focus.c };
    const r1 = Math.min(sel.r1, sel.r2), r2 = Math.max(sel.r1, sel.r2);
    const c1 = Math.min(sel.c1, sel.c2), c2 = Math.max(sel.c1, sel.c2);
    const lines = [];
    for (let r = r1; r <= r2; r++) {
      const row = rows[r];
      const parts = [];
      for (let c = c1; c <= c2; c++) {
        const col = cols[c];
        parts.push(formatCellText((row?.cells || {})[col?.id], col, users));
      }
      lines.push(parts.join("\t"));
    }
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${r2 - r1 + 1}×${c2 - c1 + 1}`);
      return true;
    } catch { return false; }
  }, [focus, selection, rows, cols, users]);

  // Paste TSV — expand rows/cols from focus. Interoperable with Sheets/Excel.
  const pasteAtFocus = useCallback(async (rawText) => {
    if (!focus || !canEdit) return false;
    const lines = rawText.replace(/\r/g, "").split("\n");
    if (lines[lines.length - 1] === "") lines.pop();
    if (lines.length === 0) return false;
    const grid = lines.map((l) => l.split("\t"));
    const startR = focus.r;
    const startC = focus.c;
    // Ensure enough rows
    let effRows = [...rows];
    let created = 0;
    while (startR + grid.length > effRows.length) {
      const r = await addRow({});
      if (!r) break;
      effRows.push(r);
      created++;
    }
    // Batch patches per row
    for (let dr = 0; dr < grid.length; dr++) {
      const row = effRows[startR + dr];
      if (!row) continue;
      const patch = {};
      for (let dc = 0; dc < grid[dr].length; dc++) {
        const col = cols[startC + dc];
        if (!col) continue;
        patch[col.id] = parseCellText(grid[dr][dc], col, users);
      }
      if (Object.keys(patch).length) await patchCells(row.id, patch);
    }
    toast.success(`Pasted ${grid.length}×${grid[0].length}${created ? ` (added ${created} rows)` : ""}`);
    return true;
  }, [focus, canEdit, rows, cols, addRow, patchCells, users]);

  useEffect(() => {
    const onKey = (e) => {
      // Ignore when a modal / another input outside the grid has focus.
      const inGrid = gridRef.current && gridRef.current.contains(document.activeElement);
      if (!focus && !inGrid) return;
      // Cell-in-edit-mode? Let its input handle keys except Escape.
      if (editingCell) {
        if (e.key === "Escape") { setEditingCell(null); e.preventDefault(); }
        return;
      }
      // Copy — attempt async write; the browser paste event handles Ctrl+V natively.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "c") { e.preventDefault(); copyFocus(); return; }
      switch (e.key) {
        case "ArrowUp":    e.preventDefault(); moveFocus(-1, 0); break;
        case "ArrowDown":  e.preventDefault(); moveFocus(1, 0);  break;
        case "ArrowLeft":  e.preventDefault(); moveFocus(0, -1); break;
        case "ArrowRight": e.preventDefault(); moveFocus(0, 1);  break;
        case "Tab":        e.preventDefault(); moveFocus(0, e.shiftKey ? -1 : 1); break;
        case "Enter":      e.preventDefault();
                           if (canEdit && focus) setEditingCell({ ...focus });
                           break;
        case "Delete":
        case "Backspace":
          if (canEdit && focus) {
            e.preventDefault();
            const row = rows[focus.r], col = cols[focus.c];
            if (row && col) patchCells(row.id, { [col.id]: emptyValueFor(col) });
          }
          break;
        case "F2":
          if (canEdit && focus) { e.preventDefault(); setEditingCell({ ...focus }); }
          break;
        default: break;
      }
    };
    // Native paste event — no permission prompt needed.
    const onPaste = (e) => {
      if (!focus || !canEdit) return;
      if (editingCell) return; // let input handle it
      const inGrid = gridRef.current && gridRef.current.contains(document.activeElement);
      if (!inGrid && !document.body.contains(document.activeElement)) return;
      const text = e.clipboardData?.getData("text/plain");
      if (!text) return;
      e.preventDefault();
      pasteAtFocus(text);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("paste", onPaste);
    };
  }, [focus, editingCell, canEdit, rows, cols, moveFocus, copyFocus, pasteAtFocus, patchCells]);

  /* ---------- List metadata ---------- */
  const saveListName = async () => {
    const name = nameDraft.trim();
    if (!name || name === list.name) { setEditingListName(false); return; }
    try { const r = await api.patch(`/sidebar-lists/${listId}`, { name }); setList(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
    setEditingListName(false);
  };
  const deleteList = async () => {
    if (!window.confirm(`Delete "${list.name}" and all its rows? This cannot be undone.`)) return;
    try {
      await api.delete(`/sidebar-lists/${listId}`);
      toast.success("Deleted");
      window.dispatchEvent(new Event("sidebar-lists-changed"));
      nav("/dash");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!list) return <PageSkeleton title cards={0} rows={8} />;
  const KindIcon = list.kind === "task_list" ? ListChecks : Table;

  return (
    <div className="min-h-full bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <KindIcon className="h-5 w-5 text-purple-500 flex-shrink-0" />
          {editingListName ? (
            <Input
              autoFocus value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveListName}
              onKeyDown={(e) => { if (e.key === "Enter") saveListName(); if (e.key === "Escape") setEditingListName(false); }}
              className="h-8 max-w-md" data-testid="list-name-input"
            />
          ) : (
            <button
              onClick={() => { if (canEdit) { setNameDraft(list.name); setEditingListName(true); } }}
              className="text-xl font-semibold text-slate-900 hover:text-purple-600 transition-colors truncate max-w-md text-left"
              data-testid="list-name"
            >{list.name}</button>
          )}
          <span className="text-[10px] uppercase tracking-wider text-slate-400 px-2 py-0.5 rounded bg-slate-100">
            {list.kind === "task_list" ? "Task List" : "Custom Table"}
          </span>
          <span className="text-xs text-slate-400 hidden md:inline">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button onClick={() => addRow({})} variant="outline" size="sm" data-testid="list-add-row-btn"><Plus className="h-4 w-4 mr-1" /> Row</Button>
            <Button onClick={() => setAddColOpen(true)} className="bg-purple-600 hover:bg-purple-700" size="sm" data-testid="list-add-column-btn"><Plus className="h-4 w-4 mr-1" /> Column</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="list-menu-btn"><MoreHorizontal className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { if (!focus) toast.message("Click a cell first, then Ctrl+V"); else toast.message("Ready — press Ctrl+V to paste from Google Sheets/Excel"); }}>
                  <ClipboardPaste className="h-4 w-4 mr-2" /> How to paste
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600" onClick={deleteList} data-testid="list-delete-btn">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete list
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Sheet keyboard hint */}
      <div className="px-6 pt-3 pb-1 text-[11px] text-slate-500 select-none">
        <span className="inline-flex items-center gap-3">
          <span><kbd className="kbd">↑↓←→</kbd> Move</span>
          <span><kbd className="kbd">Enter</kbd> Edit</span>
          <span><kbd className="kbd">Tab</kbd> Next</span>
          <span><kbd className="kbd">Ctrl</kbd>+<kbd className="kbd">C</kbd>/<kbd className="kbd">V</kbd> Copy / Paste</span>
          <span><kbd className="kbd">Del</kbd> Clear</span>
        </span>
      </div>

      {/* Sheet */}
      <div className="p-6 pt-3" ref={gridRef} tabIndex={-1}>
        <div className="rounded-md border border-slate-300 bg-white overflow-hidden shadow-sm">
          <div className="overflow-auto max-h-[calc(100vh-200px)]">
            <table className="text-[13px] border-collapse w-full">
              <thead className="bg-slate-100 border-b-2 border-slate-300 sticky top-0 z-10">
                <tr>
                  <th className="w-10 border-r border-slate-300 text-[11px] font-semibold text-slate-400 text-center" style={{ minWidth: 40 }}>#</th>
                  {cols.map((col, ci) => (
                    <ColumnHeader
                      key={col.id}
                      col={col}
                      canEdit={canEdit}
                      onEdit={() => setEditColumn(col)}
                      onMoveLeft={() => patchColumn(col.id, { move: "left" })}
                      onMoveRight={() => patchColumn(col.id, { move: "right" })}
                      onDelete={() => deleteColumn(col.id)}
                      colIndex={ci}
                      onDragStart={(e) => onColDragStart(e, col.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => onColDrop(e, col.id)}
                    />
                  ))}
                  <th className="w-12 border-l border-slate-300 bg-slate-100 text-center">
                    {canEdit && (
                      <button onClick={() => setAddColOpen(true)} className="p-1 rounded hover:bg-white text-slate-500 hover:text-purple-600" title="Add column" data-testid="table-header-add-col">
                        <Plus className="h-4 w-4 mx-auto" />
                      </button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={cols.length + 2} className="text-center py-16 text-sm text-slate-400 border-t border-slate-200">
                      {cols.length === 0 ? "Start by adding a column, then a row." : (canEdit ? "No rows yet. Click Add Row." : "No rows yet.")}
                    </td>
                  </tr>
                )}
                {rows.map((row, ri) => (
                  <tr
                    key={row.id}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onRowDrop(e, row.id)}
                    className="group hover:bg-purple-50/30"
                    data-testid={`list-row-${row.id}`}
                  >
                    <td
                      className="border-r border-b border-slate-200 bg-slate-50 group-hover:bg-slate-100 text-[11px] text-slate-400 text-center select-none px-1"
                      draggable={canEdit}
                      onDragStart={(e) => onRowHandleDragStart(e, row.id)}
                      title="Drag row-number to reorder"
                    >
                      <div className="flex items-center justify-center gap-0.5 cursor-grab active:cursor-grabbing">
                        {canEdit && <GripVertical className="h-3 w-3 opacity-30 group-hover:opacity-70" />}
                        <span>{ri + 1}</span>
                      </div>
                    </td>
                    {cols.map((col, ci) => {
                      const isFocused = focus && focus.r === ri && focus.c === ci;
                      const isInEdit = isFocused && editingCell && editingCell.r === ri && editingCell.c === ci;
                      const selected = inSelection(ri, ci);
                      return (
                        <td
                          key={col.id}
                          onMouseDown={(e) => onCellMouseDown(e, ri, ci)}
                          onMouseEnter={(e) => onCellMouseEnter(e, ri, ci)}
                          onDoubleClick={() => canEdit && setEditingCell({ r: ri, c: ci })}
                          className={`border-r border-b border-slate-200 px-0 relative select-none ${isFocused ? "ring-2 ring-inset ring-purple-500 z-[1]" : ""} ${selected && !isFocused ? "bg-purple-100/60" : ""}`}
                          style={{ minWidth: 140 }}
                          data-testid={`cell-${col.id}-${row.id}`}
                        >
                          <SheetCell
                            col={col}
                            row={row}
                            users={users}
                            isEditing={isInEdit}
                            isFocused={isFocused}
                            canEdit={canEdit}
                            onCommit={(v) => { patchCells(row.id, { [col.id]: v }); setEditingCell(null); }}
                            onExitEdit={() => setEditingCell(null)}
                            onBeginEdit={() => canEdit && setEditingCell({ r: ri, c: ci })}
                          />
                        </td>
                      );
                    })}
                    <td className="border-l border-b border-slate-200 bg-slate-50/50 px-1">
                      {canEdit && (
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => duplicateRow(row.id)} className="p-1 rounded hover:bg-slate-200 text-slate-500" title="Duplicate" data-testid={`row-duplicate-${row.id}`}>
                            <Copy className="h-3 w-3" />
                          </button>
                          <button onClick={() => deleteRow(row.id)} className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-600" title="Delete" data-testid={`row-delete-${row.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {canEdit && cols.length > 0 && (
                  <tr>
                    <td colSpan={cols.length + 2} className="border-t border-slate-200">
                      <button onClick={() => addRow({})} className="w-full text-left px-3 py-2 text-xs text-slate-400 hover:bg-purple-50 hover:text-purple-600 transition-colors flex items-center gap-1.5" data-testid="add-row-inline">
                        <Plus className="h-3 w-3" /> Add row
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddColumnDialog open={addColOpen} onClose={() => setAddColOpen(false)} onCreate={addColumn} />
      {editColumn && (
        <EditColumnDialog
          column={editColumn}
          onClose={() => setEditColumn(null)}
          onSave={async (patch) => { await patchColumn(editColumn.id, patch); setEditColumn(null); }}
        />
      )}
    </div>
  );
}

/* ==== helpers ==== */
function formatCellText(raw, col, users) {
  if (raw == null || raw === "") return "";
  if (col.type === "checkbox") return raw ? "TRUE" : "FALSE";
  if (col.type === "member") return (users.find((u) => u.id === raw) || {}).name || "";
  return String(raw);
}
function parseCellText(text, col, users) {
  const t = (text || "").trim();
  if (t === "") return col.type === "checkbox" ? false : "";
  if (col.type === "checkbox") return /^(1|true|yes|checked|✓)$/i.test(t);
  if (col.type === "number")   { const n = Number(t); return Number.isFinite(n) ? n : ""; }
  if (col.type === "member")   { const u = users.find((x) => x.name.toLowerCase() === t.toLowerCase() || x.email?.toLowerCase() === t.toLowerCase() || x.id === t); return u ? u.id : ""; }
  if (col.type === "dropdown") { const m = (col.options || []).find((o) => o.toLowerCase() === t.toLowerCase()); return m || ""; }
  return t;
}
function emptyValueFor(col) {
  if (col.type === "checkbox") return false;
  if (col.type === "member" || col.type === "dropdown") return "";
  return "";
}

/* ==== Column Header ==== */
function ColumnHeader({ col, canEdit, onEdit, onMoveLeft, onMoveRight, onDelete, onDragStart, onDragOver, onDrop }) {
  const Icon = COL_TYPE_MAP[col.type]?.icon || Type;
  return (
    <th
      draggable={canEdit}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`px-3 py-1.5 text-left font-semibold text-slate-700 border-r border-slate-300 min-w-[140px] bg-slate-100 ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-slate-500 flex-shrink-0" />
        <span className="truncate text-xs uppercase tracking-wide">{col.name}</span>
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-auto p-0.5 rounded hover:bg-white text-slate-500" data-testid={`col-menu-${col.id}`}>
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit} data-testid={`col-rename-${col.id}`}>
                <Pencil className="h-4 w-4 mr-2" /> Rename{col.type === "dropdown" ? " / Options" : ""}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMoveLeft}>
                <ChevronLeft className="h-4 w-4 mr-2" /> Move left
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMoveRight}>
                <ChevronRight className="h-4 w-4 mr-2" /> Move right
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={onDelete} data-testid={`col-delete-${col.id}`}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete column
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </th>
  );
}

/* ==== Cell renderer (view + inline edit) ==== */
function SheetCell({ col, row, users, isEditing, isFocused, canEdit, onCommit, onExitEdit, onBeginEdit }) {
  const raw = (row.cells || {})[col.id];
  // Checkbox is always inline (single click toggles)
  if (col.type === "checkbox") {
    return (
      <div className="flex items-center justify-center h-7 px-2" onClick={(e) => { e.stopPropagation(); if (canEdit) onCommit(!raw); }}>
        <input type="checkbox" readOnly checked={!!raw} className="accent-purple-600 pointer-events-none" />
      </div>
    );
  }
  if (isEditing && canEdit) {
    return <CellEditor col={col} value={raw} users={users} onCommit={onCommit} onCancel={onExitEdit} />;
  }
  // Read-only view
  return (
    <div
      className="h-7 px-2 flex items-center overflow-hidden whitespace-nowrap text-slate-800 cursor-cell"
      onDoubleClick={onBeginEdit}
    >
      <CellReadOnly col={col} value={raw} users={users} />
    </div>
  );
}
function CellReadOnly({ col, value, users }) {
  if (value == null || value === "") return <span className="text-slate-300">—</span>;
  if (col.type === "member") {
    const u = users.find((x) => x.id === value);
    return u ? <span className="truncate">{u.name}</span> : <span className="text-slate-300">—</span>;
  }
  if (col.type === "link") {
    return <a href={value} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-purple-600 hover:underline truncate flex items-center gap-1"><Link2 className="h-3 w-3" /> {value}</a>;
  }
  if (col.type === "dropdown") {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 truncate">{value}</span>;
  }
  if (col.type === "date") {
    return <span className="truncate">{value}</span>;
  }
  return <span className="truncate">{String(value)}</span>;
}

/* Inline editor per cell type */
function CellEditor({ col, value, users, onCommit, onCancel }) {
  const [v, setV] = useState(value ?? "");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); if (ref.current?.select) ref.current.select(); }, []);

  const commit = () => onCommit(v === "" ? "" : v);
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); }
    else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
  };

  if (col.type === "dropdown") {
    return (
      <select
        ref={ref} autoFocus value={v || ""} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={onKey}
        className="h-7 w-full bg-white border-0 outline-none px-2 text-[13px]"
      >
        <option value="">—</option>
        {(col.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (col.type === "member") {
    return (
      <select
        ref={ref} autoFocus value={v || ""} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={onKey}
        className="h-7 w-full bg-white border-0 outline-none px-2 text-[13px]"
      >
        <option value="">—</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
    );
  }
  if (col.type === "date") {
    return <input ref={ref} type="date" value={v || ""} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={onKey} className="h-7 w-full bg-white border-0 outline-none px-2 text-[13px]" />;
  }
  if (col.type === "number") {
    return <input ref={ref} type="number" value={v ?? ""} onChange={(e) => setV(e.target.value === "" ? "" : Number(e.target.value))} onBlur={commit} onKeyDown={onKey} className="h-7 w-full bg-white border-0 outline-none px-2 text-[13px] text-right" />;
  }
  if (col.type === "long_text") {
    return <textarea ref={ref} value={v || ""} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Escape") onCancel(); if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); commit(); } }} rows={2} className="w-full bg-white border-0 outline-none px-2 py-1 text-[13px] resize-none" />;
  }
  return <input ref={ref} type={col.type === "link" ? "url" : "text"} value={v || ""} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={onKey} className="h-7 w-full bg-white border-0 outline-none px-2 text-[13px]" />;
}

/* ==== Add / Edit Column Dialogs ==== */
function AddColumnDialog({ open, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("text");
  const [options, setOptions] = useState([""]);
  useEffect(() => { if (open) { setName(""); setType("text"); setOptions([""]); } }, [open]);
  const submit = async () => {
    if (!name.trim()) { toast.error("Column name required"); return; }
    const opts = type === "dropdown" ? options.map((o) => o.trim()).filter(Boolean) : undefined;
    await onCreate({ name: name.trim(), type, options: opts });
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="add-column-dialog">
        <DialogHeader><DialogTitle>Add column</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Column name" data-testid="add-col-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="add-col-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COL_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    <div className="flex items-center gap-2"><t.icon className="h-3.5 w-3.5" />{t.label}</div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {type === "dropdown" && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Options</label>
              <div className="space-y-1.5">
                {options.map((o, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Input value={o} onChange={(e) => setOptions(options.map((x, ix) => ix === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} />
                    <button onClick={() => setOptions(options.filter((_, ix) => ix !== i))} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => setOptions([...options, ""])} className="text-xs text-purple-600 hover:underline">+ Add option</button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-purple-600 hover:bg-purple-700" data-testid="add-col-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function EditColumnDialog({ column, onClose, onSave }) {
  const [name, setName] = useState(column.name);
  const [options, setOptions] = useState(column.options || []);
  const isDropdown = column.type === "dropdown";
  const moveOpt = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    const next = [...options];
    [next[i], next[j]] = [next[j], next[i]];
    setOptions(next);
  };
  const save = async () => {
    const patch = { name };
    if (isDropdown) patch.options = options.map((o) => o.trim()).filter(Boolean);
    await onSave(patch);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-column-dialog">
        <DialogHeader><DialogTitle>Edit column</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-col-name" />
          </div>
          {isDropdown && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Options</label>
              <div className="space-y-1.5">
                {options.map((o, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Input value={o} onChange={(e) => setOptions(options.map((x, ix) => ix === i ? e.target.value : x))} />
                    <button onClick={() => moveOpt(i, -1)} disabled={i === 0} className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /></button>
                    <button onClick={() => moveOpt(i, +1)} disabled={i === options.length - 1} className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setOptions(options.filter((_, ix) => ix !== i))} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => setOptions([...options, ""])} className="text-xs text-purple-600 hover:underline">+ Add option</button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-purple-600 hover:bg-purple-700" data-testid="edit-col-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
